import type { MetaDescriptor } from 'react-router'
// Imported (not a `public/` path) so Vite fingerprints it into
// `/assets/og-image-<hash>.png` — see the header of scripts/build-og-image.ts
// for why that matters at the CloudFront layer, and for how to regenerate it.
import ogImageUrl from './images/og-image.png'
import { OG_QUERY_CARD_PATH } from './og/card-path'
import { absoluteUrl, SITE_NAME } from './site'

// Every page title ends with this, so `og:title` can drop it: the card already
// carries the site name in `og:site_name`, and "Terms | Vital Care Finder" under
// a heading that reads "Vital Care Finder" says it twice.
//
// Exported so `fitTitle` callers compose the same string this strips — a title
// assembled with a hand-written suffix that drifted would keep the site name in
// `og:title` as well as in `og:site_name`.
export const TITLE_SUFFIX = ` | ${SITE_NAME}`

/**
 * The longest a `<title>` may be before SEMrush and Ahrefs report it as too long,
 * counted over the whole title with `TITLE_SUFFIX` included (VIT-18071).
 *
 * A character count rather than the rendered pixel width Google actually truncates
 * at (~600px in the SERP column): the crawlers that flag this measure characters,
 * so a pixel model would leave their reports red while claiming the problem was
 * fixed. It is also the cheaper approximation to be wrong about — proportional
 * width would need font metrics we do not have server-side.
 */
export const TITLE_MAX_LENGTH = 70

/**
 * The longest a `<meta name="description">` may be before Google truncates it in
 * the SERP (VIT-18200).
 *
 * Same character-count approximation, and the same reason, as `TITLE_MAX_LENGTH`
 * above. 160 is the widely-cited desktop ceiling; a description over it isn't
 * penalised, it is simply cut — but a cut we chose reads better than one Google
 * chose, and a description Google finds unhelpful is one it replaces with scraped
 * page text, which is the failure this whole ticket exists to fix.
 */
export const DESCRIPTION_MAX_LENGTH = 160

// A single character, so a truncated title spends one of its 70 on the marker.
const ELLIPSIS = '…'

/**
 * Cut `text` to `maxLength` on a word boundary and mark it as cut.
 *
 * Words, not characters, because the alternative is a title ending mid-word
 * ("Central Coast Chest Consulta…"), which reads as a rendering bug rather than as
 * a long name. Trailing separators go with the word they followed — "Associates,…"
 * dangles the comma the dropped clause belonged to.
 *
 * A first word longer than the budget has no boundary to cut on, so it is cut
 * mid-word rather than losing the whole title.
 */
const truncateToWord = (text: string, maxLength: number): string => {
  const clipped = text.slice(0, maxLength - ELLIPSIS.length)
  const lastBoundary = clipped.lastIndexOf(' ')
  const kept = lastBoundary > 0 ? clipped.slice(0, lastBoundary) : clipped

  return `${kept.replace(/[\s,;:.\-–—]+$/u, '')}${ELLIPSIS}`
}

/** The first candidate that fits `maxLength`, or the last one truncated to it. */
const fitToBudget = (maxLength: number, candidates: string[]): string => {
  const fitting = candidates.find((candidate) => candidate.length <= maxLength)

  // The last candidate is the caller's most-shed form — it has already given up
  // everything it was willing to give up, so it is the one to cut into.
  return fitting ?? truncateToWord(candidates.at(-1) ?? '', maxLength)
}

/**
 * The first of these titles that fits inside `TITLE_MAX_LENGTH`, or the last one
 * truncated if none of them do (VIT-18071).
 *
 * Callers pass what they want in descending order of preference, so each route
 * decides for itself what a too-long title should give up rather than having the
 * budget decide by chopping the end off. provider.tsx sheds the brand suffix, then
 * the practice location; see the comment there for why in that order.
 *
 * Truncation is the floor, not the plan: it fires only where a provider's own name
 * exceeds the budget on its own, and the marker is what tells a crawler (and a
 * reader) that the name continues rather than that we spell it that way.
 */
export const fitTitle = (preferred: string, ...fallbacks: string[]): string =>
  fitToBudget(TITLE_MAX_LENGTH, [preferred, ...fallbacks])

/**
 * The first of these descriptions that fits inside `DESCRIPTION_MAX_LENGTH`, or
 * the last one truncated if none of them do (VIT-18200).
 *
 * Same contract as `fitTitle`, and deliberately so: the caller passes what it
 * wants in descending order of preference and decides for itself what a too-long
 * description gives up. results.tsx sheds named providers one at a time — three,
 * two, one, none — because the names are the page-specific substance, and the last
 * one to go should be the one Google would have scraped first anyway.
 */
export const fitDescription = (preferred: string, ...fallbacks: string[]): string =>
  fitToBudget(DESCRIPTION_MAX_LENGTH, [preferred, ...fallbacks])

// Describes the site-wide card, which is what a route gets when it passes no `image`
// of its own. Routes that DO pass one must pass their own `imageAlt` with it — this
// string describes the home card's artwork and would be a lie about any other.
const OG_IMAGE_ALT = `${SITE_NAME}: find the right doctor for you`

// Pinned in the tags as well as baked into the file, because several consumers
// (X, LinkedIn) lay out the card before they have fetched the image.
const OG_IMAGE_WIDTH = '1200'
const OG_IMAGE_HEIGHT = '630'

type PageMetaOptions = {
  /**
   * The full document title, normally including the `| Vital Care Finder` suffix.
   *
   * Normally, not always: a title for which `fitTitle` had to shed the suffix to
   * stay inside `TITLE_MAX_LENGTH` arrives without it, and passes through to
   * `og:title` unchanged — the strip below is a no-op on a title that has none.
   */
  title: string
  /** The meta description, reused verbatim for `og:description`. */
  description: string
  /**
   * The path this page wants indexed — NOT necessarily the path being served.
   *
   * Omitted only when there is no such page: results.tsx and provider.tsx call
   * this from an error boundary render too (`loaderData` is undefined when the
   * loader threw), and a 404 must not claim itself as the canonical version of
   * anything. `og:url` drops out with it, for the same reason — nobody shares a
   * page that failed to load.
   *
   * Explicit rather than derived from the request, because two routes
   * deliberately point elsewhere: results.tsx sends duplicate specialty slugs to
   * their primary (`family-doctor` → `primary-care-provider`, which share a NUCC
   * code and therefore a roster), and provider.tsx sends every decorative slug
   * variant to the canonical spelling (VIT-17797). For every other route this is
   * simply the route's own path.
   *
   * Path-only, with no query string, which is the point on results.tsx: it
   * accepts arbitrary `?insurance=<uuid>` values, so each param combination is
   * otherwise a distinct indexable URL competing with the clean one.
   */
  canonicalPath?: string | undefined
}

/**
 * The card a page wants in a share preview, or neither field (VIT-18194).
 *
 * A union rather than two independent optionals, so `image` cannot be passed without
 * `imageAlt`. The default alt describes the home card's artwork — a per-page card
 * inheriting it would describe the wrong image to exactly the readers who depend on the
 * description, and that is a mistake a type can prevent rather than a comment ask for.
 *
 * `image` is a site-relative path or an absolute URL. A path, not an imported asset,
 * because the two kinds of card arrive differently: a build-time card is a fingerprinted
 * Vite import (already a `/assets/…` path), while a runtime card is rendered by the
 * `OgRenderer` Lambda and served under `/og/` (`/og/card.png?template=…`). Both are paths
 * by the time they get here, and `absoluteUrl` is applied unless the value already names an
 * origin — Open Graph requires absolute image URLs.
 */
type PageMetaCard = { image: string; imageAlt: string } | { image?: undefined; imageAlt?: undefined }

/** The copy a fixed page's card is built from — the three the generic template takes. */
export type FixedPageCardCopy = {
  /** The page's own label, rendered uppercase as the eyebrow: "Support", "Terms". */
  readonly label: string
  /** The headline. The page's own `h1`, not its `<title>` — see `fixedPageCard`. */
  readonly title: string
  /** The page's meta description, verbatim: the card and the page say the same thing. */
  readonly description: string
}

/**
 * The card for a fixed-content page (Support, Terms, Privacy, MCP Apps), as the
 * `image`/`imageAlt` pair `pageMeta` takes (VIT-18195).
 *
 * These four have no upstream record, so their card is described by its query string rather
 * than addressed by their page path — passing our own strings back to ourselves, which is
 * the fixed-content half of the renderer's contract (og/handler.server.ts). Rendered per
 * request rather than committed as four PNGs: `/og/*` is already a CloudFront behavior with
 * a week-long edge cache and is already on the deploy invalidation list, so the alternative
 * buys nothing and adds a regenerate-and-commit step every description change would need.
 * The URL carrying the copy is what cache-busts it when that copy changes.
 *
 * The headline is the page's own `h1` rather than its `<title>`, because the eyebrow already
 * carries the label the title starts with: `/terms` would otherwise render "TERMS" above
 * "Terms".
 *
 * `encodeURIComponent` rather than `URLSearchParams`, which form-encodes a space as `+`. A
 * Function URL percent-decodes `queryStringParameters` but does not undo form encoding, so
 * that spelling would reach satori intact and the card would read "Terms+of+Use". `%20`
 * survives both.
 *
 * Fixed parameter order, and `template=generic` stated even though the handler defaults to
 * it: the whole query string is the edge cache key (see the `/og/*` behavior in
 * sst.config.ts), so a reordering is a second render of an identical card.
 */
export const fixedPageCard = ({ label, title, description }: FixedPageCardCopy): PageMetaCard => {
  const query = Object.entries({ template: 'generic', label, title, description })
    .map(([name, value]) => `${name}=${encodeURIComponent(value)}`)
    .join('&')

  return {
    image: `${OG_QUERY_CARD_PATH}?${query}`,
    // Describes the CARD, as `provider.tsx`'s alt does: the headline and the wordmark are
    // what it shows, and the description under them is already the `og:description` a
    // reader without the image has. Nothing here restates it.
    //
    // A headline that already opens with the site name keeps it — "Vital Care Finder MCP
    // Server on Vital Care Finder" says it twice to the one reader who cannot see that the
    // card only shows it once.
    imageAlt: title.startsWith(SITE_NAME) ? title : `${title} on ${SITE_NAME}`,
  }
}

/**
 * The shared `<head>` tag set for an indexable page: title, description,
 * self-referencing canonical, Open Graph and Twitter card (VIT-17746).
 *
 * The canonical is absolute (VIT-18029). A relative one is valid — it resolves
 * against the document URL — but Google's own canonicalization guidance
 * recommends absolute, and three things here want it:
 *
 *  - Scraped and mirrored content, which is the real argument for this site. A
 *    provider directory with curated ratings and cost data gets republished. An
 *    absolute canonical in the copied markup credits vit.al; a relative one
 *    resolves against the scraper's domain and credits them.
 *  - Migration safety: a future protocol, domain or extra hostname would have
 *    relative canonicals resolving to whichever host the crawler happened to
 *    fetch, which defeats the tag.
 *  - No reliance on base-URL resolution — a `<base>` tag or an unexpected
 *    serving host would otherwise change every canonical on the site at once.
 *
 * `og:url` and `og:image` were already absolute, Open Graph requiring it. So
 * were the JSON-LD nodes, sitemap.ts and llms.ts. A preview stage therefore
 * emits vit.al canonicals — accepted, and the same trade-off those already make.
 * What makes it safe is that no preview is indexed: VIT-17997 stamps
 * `noindex, nofollow` on off-production stages in three independent layers (see
 * app/robots.ts), and a page a crawler has been told not to index is not one it
 * consolidates duplicates onto, so its canonical never gets acted on.
 *
 * Deliberately NOT applied to `/mcp-demo` or `/__geo`: both are `robots: noindex`,
 * and a canonical on a page asking not to be indexed is contradictory signalling.
 */
export const pageMeta = ({
  title,
  description,
  canonicalPath,
  image = ogImageUrl,
  imageAlt = OG_IMAGE_ALT,
}: PageMetaOptions & PageMetaCard): MetaDescriptor[] => {
  const socialTitle = title.endsWith(TITLE_SUFFIX) ? title.slice(0, -TITLE_SUFFIX.length) : title
  // Already-absolute passes through: a caller with a full URL has said where the card
  // lives, and prefixing the origin again would produce `https://vit.al/https://…`.
  const imageUrl = /^https?:\/\//u.test(image) ? image : absoluteUrl(image)

  return [
    { title },
    { name: 'description', content: description },
    ...(canonicalPath != null
      ? [{ tagName: 'link', rel: 'canonical', href: absoluteUrl(canonicalPath) } as const]
      : []),

    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:title', content: socialTitle },
    { property: 'og:description', content: description },
    ...(canonicalPath != null ? [{ property: 'og:url', content: absoluteUrl(canonicalPath) }] : []),
    { property: 'og:image', content: imageUrl },
    { property: 'og:image:width', content: OG_IMAGE_WIDTH },
    { property: 'og:image:height', content: OG_IMAGE_HEIGHT },
    { property: 'og:image:alt', content: imageAlt },

    // `name`, not `property`: the Twitter tags predate RDFa and are specified on
    // `name`. Only the four that carry information the og:* tags above don't
    // already cover for X's parser.
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: socialTitle },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },
    { name: 'twitter:image:alt', content: imageAlt },
  ]
}
